import { ChangeEvent, FC, MouseEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColDef, SelectionChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { AxiosError } from 'axios';
import isEqual from 'react-fast-compare';
import DeleteConfirmationModal from '../../common/DeleteConfirmationModal/DeleteConfirmationModal';
import UnsavedChangesModal from '../../common/UnsavedChangesModal/UnsavedChangesModal';
import Button from '../../components/Button/Button';
import { CaretPosition, Variant } from '../../components/Button/Button.types';
import Dropdown from '../../components/Dropdown/Dropdown';
import { Options } from '../../components/Dropdown/Dropdown.types';
import Input from '../../components/Input/Input';
import Modal from '../../components/Modal/Modal';
import Table from '../../components/Table/Table';
import { showToast } from '../../components/Toast/Toast';
import { ToastTypes } from '../../components/Toast/Toast.types';
import { ManInvConcept, ManInvConceptKeys, ManInvFrequency, ManInvFrequencyKeys } from '../../constants/ManInv';
import {
  useAddManInvGroup,
  useDeleteManInvGroup,
  useEditManInvGroup,
  useManInvGroupItems,
  useManInvGroups,
  useManInvItems,
  useUpdateManInvGroupItems,
} from '../../services/manInv/manInv.service';
import { ManInvGroup, ManInvItem } from '../../services/manInv/manInv.types';
import {
  genericErrorShowToastOptions,
  useGenericErrorMessage,
} from '../../utils/useGenericErrorMessage/useGenericErrorMessage';
import useNavigateWithToast from '../../utils/useNavigateWithToast/useNavigateWithToast';
import {
  DropdownWrapper,
  ManInvPage,
  StyledButtons,
  StyledExtendedHeader,
  StyledLink,
  StyledMessage,
  StyledTextButton,
  TextButtonsWrapper,
  Title,
} from './MandatoryInventory.styles';

const MandatoryInventory: FC = () => {
  const navigate = useNavigate();
  const navigateWithToast = useNavigateWithToast();
  const selectedManInvGridRef = useRef<AgGridReact>(null);
  const availableManInvGridRef = useRef<AgGridReact>(null);

  const { data, isError: manInvItemsIsError, error: manInvItemsError } = useManInvItems(true);
  const { data: manInvGroupsData, refetch } = useManInvGroups(true);
  const [availableManInvRowData, setAvailableManInvRowData] = useState<ManInvItem[]>([]);
  const [selectedManInvRowData, setSelectedManInvRowData] = useState<ManInvItem[]>([]);
  const [manInvGroupOptions, setManInvGroupOptions] = useState<Options[]>([]);
  const [selectedManInvGroupId, setSelectedManInvGroupId] = useState<string | undefined>(undefined);
  const { data: manInvGroupItemsData } = useManInvGroupItems(
    selectedManInvGroupId,
    selectedManInvGroupId !== undefined
  ); // Get items based on group
  const { mutate, isError, isLoading, error } = useUpdateManInvGroupItems(selectedManInvGroupId || '');
  const { mutate: addGroup } = useAddManInvGroup();
  const { mutate: editGroup } = useEditManInvGroup(selectedManInvGroupId);
  const { mutate: deleteGroup, isLoading: isDeleteLoading } = useDeleteManInvGroup();
  const allManInvItems = data?.data || [];
  const allManInvGroups = manInvGroupsData?.data || [];
  const allManInvGroupItems = manInvGroupItemsData?.data || [];

  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const checkForUnsavedChanges = () => {
    if (allManInvGroupItems?.length === selectedManInvRowData.length) {
      if (isEqual(allManInvGroupItems, selectedManInvRowData)) {
        setUnsavedChanges(false);
      } else {
        setUnsavedChanges(true);
      }
    } else {
      setUnsavedChanges(true);
    }
  };

  useEffect(() => {
    checkForUnsavedChanges();
  }, [allManInvGroupItems, selectedManInvRowData]);

  const selectAllAvailableManInvItems = (rowData: ManInvItem[]) => {
    availableManInvGridRef?.current?.api?.forEachNode((node) => {
      const matchingItem = rowData?.find(
        (selectedManInvItem) => selectedManInvItem.manInvItemId === node?.data?.manInvItemId
      );
      if (matchingItem) {
        node.setSelected(true);
      } else {
        node.setSelected(false);
      }
    });
  };

  useGenericErrorMessage([
    { isError, error },
    { isError: manInvItemsIsError, error: manInvItemsError },
  ]);

  useEffect(() => {
    setAvailableManInvRowData(allManInvItems);
  }, [data]);

  useEffect(() => {
    setSelectedManInvRowData(allManInvGroupItems);
  }, [manInvGroupItemsData]);

  useEffect(() => {
    if (selectedManInvRowData) {
      selectedManInvGridRef?.current?.api?.selectAll(); // Select rows from Selected
      selectAllAvailableManInvItems(selectedManInvRowData); // Select matching rows from Available
    }
  }, [selectedManInvRowData]);

  useEffect(() => {
    if (manInvGroupsData) {
      setManInvGroupOptions(
        allManInvGroups.map((manInvGroup: ManInvGroup) => {
          return { value: manInvGroup.id.toString(), label: manInvGroup.description };
        })
      );
    }
  }, [manInvGroupsData]);

  const handleManInvGroupSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedManInvGroupId(event.target.value);
  };

  const onSelectionChangedAvailable = (event: SelectionChangedEvent) => {
    setSelectedManInvRowData(event.api.getSelectedRows());
  };

  const onSelectionChangedSelected = (event: SelectionChangedEvent) => {
    selectAllAvailableManInvItems(event.api.getSelectedRows());
  };

  const handleSave = () => {
    setUnsavedChanges(false);
    mutate(
      selectedManInvRowData.map((item) => {
        return item.manInvItemId;
      }),
      {
        onSuccess: () => {
          navigateWithToast('/tables/mandatoryinventory', {
            label: 'Changes have been saved.',
            id: 'manInvUpdateSuccess',
          });
        },
      }
    );
  };

  const selectedManInvColumnDefs: ColDef[] = [
    {
      headerName: 'Select',
      checkboxSelection: true,
      minWidth: 70,
      width: 70,
      floatingFilter: false,
    },
    {
      headerName: 'MMSR',
      field: 'mmsRawProdNo',
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      flex: 1,
      minWidth: 180,
    },

    {
      headerName: 'Raw Product',
      field: 'rawProdNo',
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      flex: 1,
      minWidth: 180,
    },

    {
      headerName: 'Description',
      field: 'description',
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      flex: 2,
      minWidth: 180,
    },
    {
      headerName: 'Concept',
      field: 'concept',
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      flex: 2,
      minWidth: 180,
      valueFormatter: ({ value }) => ManInvConcept[value as ManInvConceptKeys] || '',
    },
    {
      headerName: 'Frequency',
      field: 'frequency',
      flex: 1,
      minWidth: 180,
      resizable: false,
      valueFormatter: ({ value }) => ManInvFrequency[value as ManInvFrequencyKeys] || '',
    },
  ];

  const availableManInvColumnDefs: ColDef[] = [
    {
      headerName: 'Select',
      sortingOrder: ['desc', 'asc', null],
      comparator: (valueA, valueB, nodeA, nodeB, isDescending) => {
        if(nodeA.isSelected() === nodeB.isSelected()) return 0
        return nodeA.isSelected() ? 1 : -1  
      },
      checkboxSelection: selectedManInvGroupId ? true : false,
      minWidth: 70,
      width: 70,
      floatingFilter: false,
    },
    {
      headerName: 'MMSR',
      field: 'mmsRawProdNo',
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      flex: 1,
      minWidth: 180,
    },
    {
      headerName: 'Raw Product',
      field: 'rawProdNo',
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      flex: 1,
      minWidth: 180,
    },
    {
      headerName: 'Description',
      field: 'description',
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      flex: 2,
      minWidth: 180,
    },
    {
      headerName: 'Concept',
      field: 'concept',
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      flex: 2,
      minWidth: 180,
      valueFormatter: ({ value }) => ManInvConcept[value as ManInvConceptKeys] || '',
    },
    {
      headerName: 'Frequency',
      field: 'frequency',
      flex: 1,
      minWidth: 180,
      resizable: false,
      valueFormatter: ({ value }) => ManInvFrequency[value as ManInvFrequencyKeys] || '',
    },
  ];

  const [addGroupModalOpen, setAddGroupModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [manInvGroupName, setManInvGroupName] = useState('');
  const [formErrors, setFormErrors] = useState('');

  const openAddModal = () => {
    setAddGroupModalOpen(true);
  };
  const openEditModal = () => {
    setEditModalOpen(true);
  };

  const openDeleteModal = () => {
    setDeleteModalOpen(true);
  };
  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
  };

  const closeModal = () => {
    setAddGroupModalOpen(false);
    setFormErrors('');
    setManInvGroupName('');
    setEditModalOpen(false);
  };

  const handleInputGroupName = (e: ChangeEvent<HTMLInputElement>) => {
    setManInvGroupName(e.target.value);
  };

  const valueArr = allManInvGroups.map((group) => {
    return group.description;
  });

  const addNewManInvGroup = () => {
    if (!manInvGroupName || manInvGroupName.length < 1) {
      setFormErrors('Please enter a Mandatory Inventory group name');
    } else if (valueArr.includes(manInvGroupName)) {
      setFormErrors('This Mandatory Inventory group name already exists. Please enter a new name');
    } else if (manInvGroupName && manInvGroupName.length > 10) {
      setFormErrors('Mandatory Inventory group name must be 10 characters or less');
    } else {
      setUnsavedChanges(false);
      addGroup(
        {
          description: manInvGroupName,
          longDescription: manInvGroupName,
        },
        {
          onSuccess: () => {
            navigateWithToast('/tables/mandatoryinventory', {
              label: 'Mandatory Inventory group has been added.',
              id: 'manInvUpdateSuccess',
            });
            refetch();
            closeModal();
          },
          onError: () => {
            showToast({
              ...genericErrorShowToastOptions,
              id: 'AddNewManInvGroupError',
              label: 'Error adding new Mandatory Inventory group.',
            });
          },
        }
      );
    }
  };

  const editManInvGroup = () => {
    if (!manInvGroupName || manInvGroupName.length < 1) {
      setFormErrors('Please enter a Mandatory Inventory group name');
    }
    if (valueArr.includes(manInvGroupName)) {
      setFormErrors('This Mandatory Inventory group name already exists. Please enter a new name');
    }
    if (manInvGroupName && manInvGroupName.length > 10) {
      setFormErrors('Mandatory Inventory group name must be 10 characters or less');
    }
    setUnsavedChanges(false);
    editGroup(
      {
        description: manInvGroupName,
        longDescription: manInvGroupName,
      },
      {
        onSuccess: () => {
          navigateWithToast('/tables/mandatoryinventory', {
            label: 'Mandatory Inventory group has been updated.',
            id: 'manInvEditSuccess',
          });
          refetch();
          closeModal();
          setManInvGroupName('');
        },
      }
    );
  };

  const handleDelete = (event: MouseEvent) => {
    event.preventDefault();
    if (selectedManInvGroupId) {
      deleteGroup(selectedManInvGroupId, {
        onSuccess: () => {
          navigateWithToast('/tables/mandatoryinventory', {
            label: 'Mandatory Inventory group has been deleted.',
            id: 'manInvGroupDeleteSuccess',
          });
          refetch();
          closeDeleteModal();
        },
        onError: (error) => {
          if ((error as AxiosError).message === 'Request failed with status code 409') {
            showToast({
              id: 'deleteManInvGroup409',
              label: 'The selected Mandatory Inventory group is currently in use.',
              toastType: ToastTypes.Error,
            });
          } else if ((error as AxiosError).message === 'apiGatewayTimeout') {
          } else {
            showToast({ ...genericErrorShowToastOptions, id: 'deleteManInvGroupOtherError' });
          }
          closeDeleteModal();
        },
      });
    }
  };

  const handleAddNew = () => {
    navigate(`/tables/mandatoryinventory/add`);
  };

  return (
    <>
      <StyledExtendedHeader>
        <StyledLink to='/tables' caret={CaretPosition.Left} label='All Tables' id='skipNavTarget' />
        <Title>Mandatory Inventory</Title>
        <DropdownWrapper>
          <Dropdown
            label='Group'
            id='manInvGroup'
            options={manInvGroupOptions}
            onChange={handleManInvGroupSelect}
          ></Dropdown>
        </DropdownWrapper>

        <TextButtonsWrapper>
          <StyledTextButton label='Add New Group' variant={Variant.Text} onClick={openAddModal} />
          {selectedManInvGroupId ? (
            <StyledTextButton label='Rename' variant={Variant.Text} onClick={openEditModal} />
          ) : (
            <></>
          )}
          {selectedManInvGroupId ? (
            <StyledTextButton label='Delete' variant={Variant.Text} onClick={openDeleteModal} />
          ) : (
            <></>
          )}
        </TextButtonsWrapper>
      </StyledExtendedHeader>
      <ManInvPage>
        <Table
          ref={selectedManInvGridRef}
          rowData={selectedManInvRowData}
          columnDefs={selectedManInvColumnDefs}
          rowSelection='multiple'
          suppressRowClickSelection={true}
          onSelectionChanged={onSelectionChangedSelected}
          showHeader={true}
          headerTitle='Selected Mandatory Inventory Items'
          headerButtons={
            selectedManInvGroupId
              ? [{ label: 'Save', isSmall: true, variant: Variant.Primary, onClick: handleSave, isLoading: isLoading }]
              : []
          }
          returnedRecords={selectedManInvRowData.length || 0}
          height={'600px'}
          width={'100%'}
        />
        <Table
          ref={availableManInvGridRef}
          rowData={availableManInvRowData}
          columnDefs={availableManInvColumnDefs}
          rowSelection='multiple'
          rowMultiSelectWithClick={selectedManInvGroupId ? true : false}
          suppressRowClickSelection={selectedManInvGroupId ? false : true}
          onSelectionChanged={onSelectionChangedAvailable}
          showHeader={true}
          headerTitle='Available Mandatory Inventory Items'
          headerButtons={[{ label: 'Add New', isSmall: true, variant: Variant.Secondary, onClick: handleAddNew }]}
          returnedRecords={allManInvItems.length || 0}
          height={'600px'}
          width={'100%'}
        />
      </ManInvPage>
      <Modal
        id='addManinvGroupModal'
        open={addGroupModalOpen}
        onClose={closeModal}
        title='Add New Mandatory Inventory Group'
      >
        <div>
          <StyledMessage>
            Please enter a description for the new Mandatory Inventory group (10 characters or less).
          </StyledMessage>
          <Input
            label=''
            value={manInvGroupName}
            onChange={handleInputGroupName}
            errorText={formErrors}
            id='addNewManInvGroup'
          />
          <StyledButtons>
            <Button variant={Variant.Secondary} label='Cancel' onClick={closeModal} />
            <Button label='Ok' onClick={addNewManInvGroup} />
          </StyledButtons>
        </div>
      </Modal>

      <Modal id='editManinvGroupModal' open={editModalOpen} onClose={closeModal} title='Edit Mandatory Inventory Group'>
        <div>
          <StyledMessage>
            Please enter new description for this Mandatory Inventory group (10 characters or less).
          </StyledMessage>
          <Input
            label=''
            value={manInvGroupName}
            onChange={handleInputGroupName}
            errorText={formErrors}
            id='editNewManInvGroup'
          />
          <StyledButtons>
            <Button variant={Variant.Secondary} label='Cancel' onClick={closeModal} />
            <Button label='Ok' onClick={editManInvGroup} />
          </StyledButtons>
        </div>
      </Modal>
      <DeleteConfirmationModal
        open={deleteModalOpen}
        onClose={closeDeleteModal}
        onDelete={handleDelete}
        isLoading={isDeleteLoading}
      />

      <UnsavedChangesModal unsavedChangesExist={unsavedChanges} />
    </>
  );
};

export default MandatoryInventory;
